import { ReactNode } from 'react';

import {
  DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL,
  ManagedIntegrationReleaseChannel,
} from '@frontend/api';
import {
  HStack,
  Badge,
  Box,
  type BoxProps,
  Image,
  Text,
  Flex,
  useMediaQuery,
  PaperlessCubeIcon,
} from '@frontend/legacy-components';

import { INTEGRATIONS } from 'modules/settings/constants';

type ResponsiveStackProps = {
  useSmallCard: boolean;
  children: ReactNode;
  padding?: string;
};

const ResponsiveStack = ({
  useSmallCard,
  children,
  ...restProps
}: ResponsiveStackProps) => {
  if (useSmallCard) {
    return (
      <Flex flexDirection="column" {...restProps}>
        {children}
      </Flex>
    );
  }
  return <HStack {...restProps}>{children}</HStack>;
};

type DividerProps = {
  useSmallCard: boolean;
  children?: ReactNode;
};

const Divider = ({ useSmallCard, children }: DividerProps) => {
  const height = useSmallCard ? '1px' : '52px';
  const width = useSmallCard ? '120px' : '1px';
  const padding = () => ({
    paddingX: useSmallCard ? '0px' : '8px',
    paddingY: useSmallCard ? '12px' : '0px',
  });

  return (
    <Box {...padding()}>
      <Box height={height} width={width} bgColor="separator.border">
        {children}
      </Box>
    </Box>
  );
};

type AuthorProps = {
  developedByPaperlessParts: boolean;
  author?: string;
};

const Author = ({
  developedByPaperlessParts,
  author = 'Paperless Parts',
}: AuthorProps) => {
  if (!developedByPaperlessParts) {
    return (
      <Text
        color={author ? 'feature.secondary' : 'feature.placeholder'}
        fontWeight="default"
      >
        {author || 'Author'}
      </Text>
    );
  }

  return (
    <Flex align="center">
      <Text size="md" fontWeight="heavy" color="brand.secondary" noOfLines={1}>
        Paperless Parts
      </Text>
      <PaperlessCubeIcon
        marginLeft="2px"
        width="22px"
        height="22px"
        color={'brand.secondary'}
      />
    </Flex>
  );
};

type LogoImageProps = {
  imageUrl: string;
};

const LogoImage = ({ imageUrl }: LogoImageProps) => (
  <Image
    src={imageUrl}
    width="120px"
    minWidth="120px"
    height="50px"
    objectFit="contain"
    alt="Logo"
  />
);

const integrationCardStyles = (
  onClick: (() => void) | undefined,
): BoxProps => ({
  backgroundColor: '#fff',
  minHeight: '80px',
  width: '100%',
  borderRadius: '8px',
  border: '1px',
  borderColor: 'separator.border',
  marginBottom: '24px',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  transitionDuration: 'normal',
  transitionProperty: 'common',
  cursor: onClick ? 'pointer' : 'default',

  _hover: {
    backgroundColor: onClick ? 'interaction.surfaceHover' : '',
  },
  _active: {
    backgroundColor: onClick ? 'interaction.surfaceActive' : '',
  },
  _focusVisible: {
    outline: 'none',
    boxShadow: 'focus',
  },
});

type CardProps = {
  name?: ReactNode;
  onClick?: () => void;
  children: ReactNode;
};

const Card = ({ name, onClick, children }: CardProps) => (
  <Box
    data-fs-id="open-managed-integration-drawer-settings"
    {...integrationCardStyles(onClick)}
    as={name === INTEGRATIONS.QUICKBOOKS_CARD_NAME ? undefined : 'button'}
    onClick={onClick}
  >
    {children}
  </Box>
);

type ManagedIntegrationCardProps = {
  imageUrl: string;
  name?: ReactNode;
  description?: ReactNode;
  onClick?: () => void;
  children?: ReactNode;
  author?: string;
  releaseChannel?: ManagedIntegrationReleaseChannel | null;
  developedByPaperlessParts?: boolean;
};

const ManagedIntegrationCard = ({
  imageUrl,
  name,
  description,
  onClick = undefined,
  children,
  author = 'Paperless Parts',
  releaseChannel = null,
  developedByPaperlessParts = false,
}: ManagedIntegrationCardProps) => {
  const [isMobile] = useMediaQuery('(max-width: 640px)');
  const [min] = useMediaQuery('(min-width: 900px)');
  const [max] = useMediaQuery('(max-width: 955px)');
  const isEdgeCase = min && max;
  const useSmallCard = isEdgeCase || isMobile;

  return (
    <Card name={name} onClick={onClick}>
      <ResponsiveStack
        useSmallCard={useSmallCard}
        padding={useSmallCard ? '16px 16px 12px 20px' : '0px 0px 0px 12px'}
      >
        <ResponsiveStack useSmallCard={useSmallCard}>
          <LogoImage imageUrl={imageUrl} />
          <Divider useSmallCard={useSmallCard} />
        </ResponsiveStack>

        <Flex flexDirection="column" alignItems="left">
          <HStack justifyContent="left" spacing="4px" textAlign="left">
            <Text
              size="lg"
              color={name ? 'feature.primary' : 'feature.placeholder'}
              fontWeight="heavy"
              wordBreak="break-all"
            >
              {name || '(Software name)'}
            </Text>

            <Text size="md" color="feature.secondary">
              by
            </Text>

            <Author
              developedByPaperlessParts={developedByPaperlessParts}
              author={author}
            />
            {releaseChannel && (
              <Badge
                colorScheme={
                  releaseChannel == DEFAULT_MANAGED_INTEGRATION_RELEASE_CHANNEL
                    ? 'success'
                    : 'warning'
                }
              >
                {releaseChannel}
              </Badge>
            )}
          </HStack>

          <Text
            color={description ? 'feature.secondary' : 'feature.placeholder'}
            textAlign="left"
          >
            {description || '(Integration description)'}
          </Text>
        </Flex>
      </ResponsiveStack>

      {children}
    </Card>
  );
};

export default ManagedIntegrationCard;
